import Logger from "../Logger";
import { tagFromName } from "./tagFromName";
import BaseModelMap from "./BaseModelMap";
import BaseObject, { enumCompareStatus, newId } from "./BaseObject";
import ObjectFactory from "./ObjectFactory";
import type Page from "./Page";
import TreeViewModel from "./TreeviewModel";
import { getPostfixFromString, getPrefixFromString, getSizeFromString } from "../models/utils";
import type Room from "./Room";
import { type State } from "../State";
import { itx } from "../translationItx/itx";
import type Channel from "./Channel";
import { channelTypeInfo } from "./ChannelType";
import { PrefixMode } from "./SettingsEnums";
import type Terminal from "./Terminal";
import type Actor from "./Actor";
import { type C1_BaseObject } from "../../ConceptOne/src/model/C1_BaseObject";
import ErrorItems from "./ErrorItems";
import type Group from "./Group";
import { modelHeaderText, modelHeaderTag, type ModelHeaderContext } from "./ModelHeader";
export type { ModelHeaderContext } from "./ModelHeader";

const logger = new Logger();

export enum ErrorCode {
  unknown = 0,
  channelTypeNotSeted = 1,
}

export class executePaste_Parameters {
  parent: BaseModel;
  isCopyRootObj: boolean;
  page?: Page;

  constructor(parent: BaseModel, isCopyRootObj: boolean, page?: Page) {
    this.parent = parent;
    this.isCopyRootObj = isCopyRootObj;
    this.page = page;
  }
}
export default class BaseModel extends BaseObject {
  /** Returns the document tag according to the supplied naming convention. */
  headerTag(context: ModelHeaderContext): string {
    return modelHeaderTag(this, context);
  }

  /** Returns the document heading, including the model's default name. */
  headerText(context: ModelHeaderContext): string {
    return modelHeaderText({ tag: this.headerTag(context), name: this.nameOfDefaultName }, context);
  }

  toString(): string {
    return `${this.tag} ${this.name}`;
  }

  treeViewModel = new TreeViewModel(this);

  //////////////////////////////////////////////////////////////////

  get isBaseModel(): boolean {
    return true;
  }

  get isBaseModelMap(): boolean {
    return false;
  }

  public get isScene(): boolean {
    return false;
  }
  public get isMultiplexer(): boolean {
    return false;
  }
  public get isChannel(): boolean {
    return false;
  }

  public get isTerminal(): boolean {
    return false;
  }

  public get isFuse(): boolean {
    return false;
  }

  public get isButtonChannel(): boolean {
    return false;
  }

  get isActorChannel(): boolean {
    return false;
  }

  get isLogicChannel(): boolean {
    return false;
  }

  get isLogicInputChannel(): boolean {
    return false;
  }

  get isLogicOutputChannel(): boolean {
    return false;
  }

  public get isActor(): boolean {
    return false;
  }

  public get isButton(): boolean {
    return false;
  }
  public get isSensor(): boolean {
    return false;
  }

  public get isDecentralActor(): boolean {
    return false;
  }

  public get isConsumer(): boolean {
    return false;
  }

  public get isFloor(): boolean {
    return false;
  }

  public get isGroup(): boolean {
    return false;
  }

  public get isRoom(): boolean {
    return false;
  }

  public get isDevice(): boolean {
    return false;
  }

  public get isCabinet(): boolean {
    return false;
  }

  public getIsDevice(): boolean {
    return false;
  }

  public get isConnectableObject(): boolean {
    return false;
  }

  get parentRoom(): Room | undefined {
    if (this.parent) {
      if (this.parent?.className == "Room") {
        return this.parent as Room;
      }
      if (this.parent instanceof BaseModel) {
        return this.parent.parentRoom;
      }
    }
    return undefined;
  }

  get parentGroup(): Group | undefined {
    if ((this.parent as BaseModel)?.isGroup) {
      return this.parent as Group;
    }
    return undefined;
  }

  private _warnings?: ErrorItems | undefined;

  isTemporary?: boolean;

  ////////////////////////////////////////////////////////////////////////////

  _errors?: ErrorItems | undefined;

  public get errors(): ErrorItems | undefined {
    if (this.getErrors() != undefined) {
      return this.getErrors();
    }
    if (this._errors) {
      if (Object.keys(this._errors).length == 0) {
        return undefined;
      }
    }

    return this._errors;
  }

  getErrors(): ErrorItems | undefined {
    return undefined;
  }

  invalidatParentErrorStatus() {
    const p = this.parent as BaseModel;
    if (p) {
      let oldErrorInChild = p._errorInChild;
      p._errorInChild = undefined;
      if (oldErrorInChild !== p._errorInChild) {
        // notify the tree node UI so it re-renders and re-reads the now invalidated errorInChild
        p.raiseOnChangeUI("errorInChild");
      }
      p.invalidatParentErrorStatus();
    }
  }

  public set errors(value: ErrorItems | undefined) {
    // toMuchGroupAdresses is retired and must never resurface, even from old saved projects
    if (value?.toMuchGroupAdresses != undefined) {
      value = { ...value };
      delete value.toMuchGroupAdresses;
    }

    // Errors are a calculated technical status, not a user-editable property.
    // Do not call beforeChange()/changed(): recalculating errors must not create undo steps.
    if (value && Object.keys(value).length == 0) {
      value = undefined;
    }

    if (this._errors != value) {
      this._errors = value;

      if (!state.isLoadingFromEstadaStorage) {
        this.invalidatParentErrorStatus();
        this.raiseOnChangeUI("errors");
      }
    }
  }

  //
  // Returns true when the object's own errors change.
  //
  // NOTE: operate on this._errors (the real backing store), not on this.errors,
  // because subclasses (e.g. Signal) override getErrors() to return a computed/merged
  // copy - mutating that copy would silently do nothing.
  addErrors(errorItems: ErrorItems): boolean {
    let res = false;

    if (this._errors) {
      for (let key in errorItems) {
        res ||= this._errors[key] != undefined;
      }
    } else {
      res = true;
    }

    this.errors = { ...this._errors, ...errorItems };
    return true;
  }

  //
  // Returns true when an own error was removed.
  //
  removeErrors(errorItems: ErrorItems): boolean {
    let res = false;
    if (this._errors) {
      for (let key in errorItems) {
        delete this._errors[key];
        res = true;
      }
      if (res && !this.hasErrors) {
        this.invalidatParentErrorStatus();
      }
    }
    return res;
  }

  flagErrors(errorItems: ErrorItems, errorCondition: boolean) {
    // The errors setter invalidates parent status and emits the single UI event.
    if (errorCondition) {
      this.addErrors(errorItems);
    } else {
      this.removeErrors(errorItems);
    }
  }

  get hasErrors(): boolean {
    // Use the (possibly overridden) `errors` getter rather than the raw `_errors`
    // backing field: some subclasses (e.g. ConnectableObject, Signal) merge in
    // additional errors dynamically that never get written to `_errors`.
    const errors = this.errors;
    for (const key in errors) {
      if (errors[key as keyof ErrorItems] != undefined) {
        return true;
      }
    }

    return false;
  }

  get errorsAsString(): string {
    let res = "";
    for (const error in this.errors) {
      res += error + "\n";
    }

    return res;
  }

  ////////////////////////////////////////////////////////////////////////////

  //
  // Diese Funktion kumuliert den Fehlerstatus von allen Kindobjekten rekursiv
  // dabei findet keine Berechung von eigenen Fehlern der jeweiligen Objekte statt
  //
  cumulateChildObjectsErrorStatus(): boolean {
    let res = false;

    const props = this.getPersistentProperties();
    for (let fullkey of props) {
      if (fullkey.startsWith("oref:")) {
        fullkey = fullkey.split(":")[1];
      }

      const attr: any = (this as any)[fullkey] as any;
      if (attr?.isBaseModelMap) {
        const baseModelMap = attr as BaseModelMap<BaseModel>;
        if (baseModelMap.isPersistent && baseModelMap.cumulateChildObjectsErrorStatus()) {
          res = true;
          break;
        }
      }
    }

    return res;
  }

  // -----------------------------------------------------------------------------
  // errorInChild
  // -----------------------------------------------------------------------------
  _errorInChild?: boolean | undefined;
  public get errorInChild(): boolean {
    if (this._errorInChild === undefined) {
      this._errorInChild = this.cumulateChildObjectsErrorStatus();
    }
    return this._errorInChild || false;
  }

  public set errorInChild(value: boolean | undefined) {
    // Derived cache only. It is invalidated and recalculated internally and must never create an undo step.
  }

  // -----------------------------------------------------------------------------

  /////////////////////////////////////////////////////////////////////////////////

  checkOwnErrors() {}

  findAllErrorInChildObjects(): Array<BaseModel> {
    const res = this.findAllErrorsInChildErrorsInPropertyObjects();
    this._errorInChild = res.length > 0;

    this.checkOwnErrors();
    if (this.hasErrors) {
      res.push(this);
    }

    return res;
  }

  findAllErrorsInChildErrorsInPropertyObjects(): Array<BaseModel> {
    let res = new Array<BaseModel>();

    const props = this.getPersistentProperties();
    for (let fullkey of props) {
      if (fullkey.startsWith("oref:")) {
        fullkey = fullkey.split(":")[1];
      }

      const attr: any = (this as any)[fullkey] as any;
      if (attr?.isBaseModelMap) {
        const baseModelMap = attr as BaseModelMap<BaseModel>;
        if (baseModelMap.isPersistent) {
          const res1 = baseModelMap.findAllErrorInChildObjects();
          res = res.concat(res1);
        }
      }
    }

    return res;
  }

  ////////////////////////////////////////////////////////////////////////////

  static monitorDelete = false;

  ////////////////////////////////////////////////////////////////////////////

  getParentCollectionName(): string | undefined {
    logger.devError(`getParentCollectionName not defined ${this.className}`);
    return undefined;
  }

  // Technical ETS synchronization metadata. It intentionally bypasses undo tracking.
  etsId: string = "";
  etsSyncTimestamp?: number;

  // -----------------------------------------------------------------------------
  // extData
  // -----------------------------------------------------------------------------
  _extData?: any;
  get extData(): any | undefined {
    return this._extData;
  }

  set extData(newValue: any | undefined) {
    if (this._extData === newValue) return;
    this.beforeChange("extData");
    this._extData = newValue;
    this.changed("extData");
  }

  // -----------------------------------------------------------------------------

  private _selectedValue = false;

  get _selected(): boolean {
    return this._selectedValue;
  }

  set _selected(newValue: boolean) {
    this._selectedValue = newValue;
  }

  //
  // state of the checkbox (in the Tree or List) in the GlobalSelectionPane
  //
  multiSelected: boolean | undefined;

  // has to be getter here because of: ReferenceError: state is not defined
  get maxNameCharacters(): number {
    return state.maxCharLimit.text;
  }
  get maxTagCharacters(): number {
    return state.maxCharLimit.tag; // for now the tagsize for all objects is 5 characters
  }

  get selected(): boolean {
    return this._selected;
  }
  set selected(newValue: boolean) {
    this.setSelected(newValue);
  }

  highlighted?: boolean;

  private _connectionFilter?: Partial<Record<string, boolean>>;

  /** Transient connection filter; intentionally excluded from persistence and undo. */
  get connectionFilter(): Partial<Record<string, boolean>> | undefined { return this._connectionFilter; }
  set connectionFilter(value: Partial<Record<string, boolean>> | undefined) {
    if (this._connectionFilter === value) return;
    this._connectionFilter = value;
    this.raiseOnUpdateUi("connectionFilter");
  }

  getIconUrl(): string | undefined {
    return undefined;
  }

  setSelected(newValue: boolean) {
    if (this._selected != newValue) {
      this.onSelect(newValue);

      this._selected = newValue;
      if (this._selected) {
        state.selectionManager.selectedObject = this;
      }
      this.raiseOnChange("selected");
    }
  }

  onSelect(newValue: boolean) {}

  getPersistentProperties(): string[] {
    return ["etsId", "etsSyncTimestamp", "extData", "errors", ...super.getPersistentProperties()]; // no itx
  }

  override setMonitorForUndo(value: boolean, visited = new Set<BaseObject>()) {
    if (visited.has(this)) return;
    super.setMonitorForUndo(value, visited);

    for (const fullName of this.getPersistentProperties()) {
      const name = fullName.startsWith("oref:") ? fullName.split(":")[1] : fullName;
      const child = (this as any)[name];
      if (child instanceof BaseObject) child.setMonitorForUndo(value, visited);
    }
  }

  static createNew(): BaseModel {
    logger.error("createNew not implemenet for class");
    return new BaseModel();
  }

  async paste(data: any) {
    try {
      logger.log("paste", data);
    } catch (ex) {
      logger.error(ex);
    }
  }

  copy(): BaseObject | undefined {
    const json = this.toJSON();
    const copyObj = ObjectFactory.createObject(this.className);
    if (copyObj) {
      copyObj.fromJSON(json);
      copyObj.id = newId();
    }
    return copyObj;
  }

  delete(withoutUpdate?: boolean): Array<BaseObject> {
    this.selected = false;
    this.isDeleting = true;

    if (this.parent?.className == "BaseModelMap") {
      const map = this.parent as BaseModelMap<BaseModel>;
      map.remove(this, withoutUpdate);
    } else {
      // generic remove object from parent collection
      const pcn = this.getParentCollectionName();
      if (pcn && this.parent) {
        const pCollection = (this.parent as any)[pcn] as BaseModelMap<BaseModel>;
        if (pCollection) {
          if (BaseModel.monitorDelete) {
            if (!pCollection.deletedItems) {
              pCollection.deletedItems = new Map();
            }
            pCollection.deletedItems.set(this.key, this);
          }

          pCollection.remove(this, withoutUpdate);
        } else {
          logger.error("unkown parent collection in delete");
        }
      }
    }

    if (state.selectionManager.selectedObject == this) {
      state.selectionManager.selectedObject = undefined;
    }

    if ((state.currentEditObject.value as any) == this) {
      state.currentEditObject.value = undefined;
    }

    return super.delete();
  }

  ////////////////////////////////////////////////////////////////////////////

  //
  // returns for now only "Hauptschaltschrank" for a single Cabinet
  //
  get nameOfDefaultName(): string {
    return this.name;
  }

  // to check if deletion is allowed
  canBeDeletedInteractive(): boolean {
    return true;
  }
  // if to ask bevore delete give the text here
  getDeleteConfirmText(): string | undefined {
    return undefined;
  }

  canBeEditedInteractive(): boolean {
    return true;
  }

  canBeCopiedInteractive(): boolean {
    return true;
  }

  getAllTagsFromSameTypeWithoutOwn(): Array<string> | undefined {
    return undefined;
  }

  ////////////////////////////////////////////////////////////////////////////

  toPersistentData(): any {
    const data: any = {};

    if (this.c1) {
      data.c1 = this.c1.toJSON();
      data.c1ClassName = this.c1.className;
    }
    const props = this.getPersistentProperties();
    props.forEach((fullkey) => {
      let key = fullkey;
      if (key.startsWith("oref:")) {
        key = key.split(":")[1];
      }

      const attr: any = (this as any)[key] as any;
      if (attr != undefined) {
        if (attr.saveToEstadaStorage) {
          data[fullkey] = attr.id;
        } else if (attr.toPersistentData) {
          data[key] = attr.toPersistentData();
        } else if (attr.toJSON) {
          const a = attr.toJSON();
          if (a) {
            attr.isSaved = true;
            data[key] = a;
          }
        } else if (typeof attr == "object") {
          // logger.error(`toPersistentData object property: ${key}`, attr, this);
          data[key] = attr;
        } else {
          data[key] = attr;
        }
      }
    });
    return data;
    // throw new Error('Method "toJSON()" must be implemented.');
  }

  override createUndoSnapshot(): any {
    return this.toPersistentData();
  }

  override restoreUndoSnapshot(data: any) {
    this.restorePersistentData(data);
  }

  loadC1FromPersistentData(data: any): any {
    if (data?.c1) {
      if (!data.c1ClassName) throw new Error(`Missing C1 facet class for ${this.className} ${this.id}`);
      const currentC1Facet = this.c1;
      if (currentC1Facet && currentC1Facet.className === data.c1ClassName) {
        currentC1Facet.fromJSON(data.c1);
        return;
      }
      this.c1?.detachOwner();
      const cn = "c1:" + data.c1ClassName;
      const newC1Object = ObjectFactory.createObject(cn, data.c1, this) as unknown as C1_BaseObject<any> | undefined;
      this.c1 = newC1Object;
    }
  }

  fromPersistentData(data: any): any {
    const to = this as any;
    this.loadC1FromPersistentData(data);

    this.isLoadedFromPersistence = true;
    for (const name in data) {
      if (name === "c1ClassName") continue;
      if (name === "collectionIndex") continue;
      if (data[name] != undefined) {
        try {
          if (!name.startsWith("oref:")) {
            if (to[name] && to[name].loadFromEstadaStorage) {
              // do nothing, is handled below
            } else if (to[name] && to[name].fromJSON) {
              //
              // allow "fromJSON" to return a complete new object
              // unsed for "DeviceClass" at the time of writing.
              // MWE 20.3.2023
              //
              const newObj = to[name].fromJSON(data[name], to);
              if (newObj) {
                to[name] = newObj;
              }
            } else {
              if (name != "className") {
                if (data.hasOwnProperty(name)) {
                  to[name] = data[name];
                }
              }
            }
          }
        } catch (ex) {
          logger.error(
            `Error in fromPersistentData for property ${name} with value ${data[name]}: ${(ex as any).message}`,
            ex,
          );
        }
      }
    }
  }

  restorePersistentData(data: any) {
    const target = this as any;
    for (const fullName of this.getPersistentProperties()) {
      if (fullName.startsWith("oref:")) continue;
      const name = fullName;
      if (name === "className" || Object.prototype.hasOwnProperty.call(data, name)) continue;
      const current = target[name];
      if (!current?.isBaseModelMap && !current?.loadFromEstadaStorage) {
        target[name] = undefined;
      }
    }
    this.fromPersistentData(data);
  }

  async saveToEstadaStorage(
    parentCollection: BaseModelMap<BaseModel> | undefined,
    saveItem: (item: BaseModel) => Promise<boolean>,
    parentObject?: BaseModel, // for object with a single reference (room.page)
  ): Promise<boolean> {
    // save "atomar" data of this object

    try {
      if (this.isSaved) {
        logger.error(`Error in saveToEstadaStorage. try to save twice:  ${this.className} ${this.tag}  ${this.name}`);
        return false;
      }

      //
      // wenn ein Objekt gespeichert wird,
      // wir es ab dann wie vom Server geladen betrachtet
      // und insbesondere beim Löschen in die Liste der gelöschten Objekte aufgenommen
      //
      this.isLoadedFromPersistence = true;

      const data = this.toPersistentData();
      if (parentCollection) {
        data.parentId = parentCollection.id;
        data.collectionIndex = parentCollection.indexOf(this);
      } else if (!parentObject) {
        logger.error("parentCollection not set", this);
      }
      await saveItem(data);

      // save sub collections and sub objects

      const props = this.getPersistentProperties();
      for (let i = 0; i < props.length; i++) {
        const fullkey = props[i];
        let key = fullkey;
        let orefParent: BaseModel | undefined;
        if (key.startsWith("oref:")) {
          key = key.split(":")[1];
          orefParent = this;
        }

        const attr: any = (this as any)[key] as any;
        if (attr != undefined) {
          if (attr.saveToEstadaStorage) {
            // logger.log(`save property ${key}`, attr, this);
            // A directly held persistent object is a single-reference child of
            // this model even when its property does not use the legacy oref prefix.
            const childParent = orefParent ?? this;
            await attr.saveToEstadaStorage(undefined, saveItem, childParent);
          }
        }
      }
    } catch (ex) {
      logger.error(`Error in BaseModel.saveToEstadaStorage ${(ex as any).message}`, ex);
      return false;
    }

    this.isSaved = true;

    return true;
  }

  async loadFromEstadaStorage(
    data: any | undefined,
    parent: BaseModel | undefined,
    loadData: (id: string | undefined, parentId: string | undefined) => any,
  ): Promise<boolean> {
    if (data == undefined) {
      data = await loadData(this.id, undefined);
    }

    if (data) {
      this.fromPersistentData(data);
    } else {
      logger.error(`loadFromEstadaStorage: loadData failed: ${this.className} ${this.id}`);
      return false;
    }

    logger.log(`loadFromEstadaStorage ${this.className} ${this.tag} ${this.name} ${this.id}`);

    // load sub collections and sub objects
    const props = this.getPersistentProperties();
    for (let i = 0; i < props.length; i++) {
      const fullkey = props[i];
      let key = fullkey;
      let isSubObject = false;
      if (key.startsWith("oref:")) {
        key = key.split(":")[1];
        isSubObject = true;
      }

      const attr: any = (this as any)[key] as any;
      const subId = data[fullkey];
      if (subId) {
        if (attr?.loadFromEstadaStorage) {
          attr.id = subId;
          await attr.loadFromEstadaStorage(undefined, this, loadData);
        } else if (isSubObject) {
          const subObjData = await loadData(subId, undefined);
          if (subObjData) {
            const subObj = ObjectFactory.createObject(subObjData.className);
            if (subObj) {
              (this as any)[key] = subObj;
              subObj.id = subId;
              subObj.parent = this;
              await subObj.loadFromEstadaStorage(undefined, this, loadData);
            }
          } else {
            logger.error(`Error loading data for id=${subId}`);
          }
        }
      }
    }

    return true;
  }

  getRelativeTag(relativeObject: BaseModel): string {
    return this.getRelativeTagIntern(relativeObject, false);
  }

  private getRelativeTagIntern(relativeObject: BaseModel, isParentObject: boolean): string {
    if (this.parent) {
      let t = this.tagPrefix;

      if (this.isChannel) {
        const channel = this as any as Channel;
        if (channel.knxChannel) {
          t = this.parent.tagPrefix + this.parent.tag + "." + channel.knxChannel.shortName;
        } else {
          //
          // return no channel tag if we dont have a knx channel yet
          //
          t = this.parent.tagPrefix + this.parent.tag;
        }
        // } else if (this.className == "Terminal" || this.className == "Fuse") {
        //   t = this.parent.tagPrefix + this.parent.tag;
      } else {
        //
        // we dont start tags with a "."
        // "." are used as "sub spearator" or in single/dot modw
        //

        if (t == ".") {
          t = "";
        }

        t += this.tag;
      }

      const useUniqueTag =
        state.projectMetadata.uniqueTagsForProject && (this.isConsumer || this.isButton || this.isButtonChannel);

      if ((this.isFuse || useUniqueTag) && !isParentObject) {
        let relativeParentTag = "";

        if (this.isButtonChannel) {
          relativeParentTag = (this.parent.parent as BaseModel)?.getRelativeTagIntern(relativeObject, true) || "";
        } else {
          relativeParentTag = (this.parent as BaseModel)?.getRelativeTagIntern(relativeObject, true) || "";
        }

        if (relativeParentTag) {
          const pp = ` (${relativeParentTag})`;
          return t + pp;
        } else {
          return t;
        }
      } else {
        //
        // Hirachical fulltag
        //

        if (this.isChannel) {
          const channel = this as any as Channel;
          if (channel.knxChannel) {
            t = channel.parent?.tag + "." + channel.knxChannel.shortName;
          } else {
            if (channelTypeInfo(channel.channelType).isSupply) {
              const actor = channel.parent as Actor;
              const relativeParentTag = (actor.parent as BaseModel)?.getRelativeTagIntern(relativeObject, true) || "";

              t = actor.supplyText || "";
              if (relativeParentTag) {
                t = relativeParentTag + "." + t;
              }
              return t;
            } else {
              t = channel.parent?.tag + "." + channel.tag;
            }
          }
        } else if (this.isTerminal) {
          const terminal = this as any as Terminal;
          t = terminal.parent?.tag + ":" + terminal.tag;
        }

        const thisParts = this.getFullTagParts();
        const otherParts = relativeObject.getFullTagParts();
        const thisPartsNew = new Array<any>();

        let different = false;
        for (let i = 0; i < thisParts.length; i++) {
          if (i >= otherParts.length || thisParts[i].tag != otherParts[i].tag || different) {
            different = true;
            thisPartsNew.push(thisParts[i]);
          }
        }

        const dotMode = state.settings.prefixMode == PrefixMode.DOT;

        let res = "";
        let first = true;
        thisPartsNew.forEach((part) => {
          if (dotMode && first) {
            res = part.tag;
          } else {
            res = res + part.prefix + part.tag;
          }
          first = false;
        });

        if (res.startsWith(".")) {
          res = res.substring(1);
        }
        if (res.endsWith(".")) {
          res = res.substring(0, res.length - 1);
        }

        return res;
      }
    }

    return this.tag;
  }

  //////////////////////////////////////////////////////
  ///////////// for copy

  getExtendedCopyCollection(startItem: BaseModel): Array<BaseModel> {
    return [];
  }

  //////////////////////////////////////////////////////
  ///////////// for paste
  mapAfterCopy() {
    if (this.propertyId) {
      // do nothing: id is depended on parent
    } else {
      super.mapAfterCopy();
    }
  }
  async pasteItem(params: { item: BaseModel; allItems: Array<BaseModel> }): Promise<Array<BaseModel>> {
    logger.error(`pasteItem not implemented for class ${this.className}`);
    return [];
  }

  async executePaste(params: executePaste_Parameters): Promise<Array<BaseModel>> {
    const { parent, isCopyRootObj } = params;
    return [this];
  }

  updateTagInPaste(parentCollection: BaseModelMap<BaseModel>) {
    if (true && state.projectMetadata.uniqueTagsForProject) {
      this.tag = state.nameAndTagManager.generateTag(this, false);
    } else {
      let tag = this.tag;

      const objectInParent = parentCollection.sortBy("tag");
      tag = state.noDoubleValue.getCopiedStringText(tag);
      objectInParent.forEach((item) => {
        if (item.tag == tag) {
          tag = state.noDoubleValue.getCopiedStringText(tag);
        }
      });
      this.tag = tag;
    }
  }

  updateNameInPaste(parentCollection: BaseModelMap<BaseModel>) {
    let name = this.name;

    const roomsInFloor = parentCollection.sortBy("name");
    name = state.noDoubleValue.getCopiedStringText(name);
    roomsInFloor.forEach((item) => {
      if (item.name == name) {
        name = state.noDoubleValue.getCopiedStringText(name);
      }
    });
    this.name = name;
  }

  moveToNewParent(newParent: BaseModel): boolean {
    const parentCollectionName = this.getParentCollectionName();

    if (parentCollectionName && this.canBeMovedToNewParent(newParent)) {
      const oldParent = this.parent as BaseModel;
      const lastParentCollection = (oldParent as any)[parentCollectionName];
      const newParentCollection = (newParent as any)[parentCollectionName];

      if (lastParentCollection && newParentCollection) {
        const move = () => {
          lastParentCollection.remove(this);
          this.parent = newParent;
          newParentCollection.add(this);
          return true;
        };

        if (state.undoManager.undoStepIsOpen) return move();

        const objectDescription = `${this.fullTag} ${this.name}`.trim() || this.className;
        const oldParentDescription = `${oldParent.fullTag} ${oldParent.name}`.trim() || oldParent.className;
        const newParentDescription = `${newParent.fullTag} ${newParent.name}`.trim() || newParent.className;
        const description =
          `Verschieben ${objectDescription} von ${oldParentDescription} ` + `nach ${newParentDescription}`;

        return state.undoManager.runAsSingleStep(description, move);
      }
    }

    return false;
  }
  canBeMovedToNewParent(newParent: BaseModel) {
    return true;
  }

  get tag2(): string {
    let p = this.parent as BaseModel;
    if (this.isChannel) {
      p = p.parent?.parent as BaseModel;
      if (p) {
        return this.getRelativeTag(p);
      }
    }

    return this.tag;
  }

  generateTagFromName() {
    if (this.tag.startsWith("@")) {
      logger.devError(`tag starts with @, do not generate new tag from name: ${this.tag}`);
      return;
    }

    this.tag = tagFromName(this.name, this.maxTagCharacters, this.getAllTagsFromSameTypeWithoutOwn());
  }

  generateNewNameFromTag() {
    let name = "";
    const tag = this.tag;
    const thenum = tag.replace(/^\D+/g, "");
    name = itx("components." + this.className, this.className) + " " + thenum;
    this.name = name;
  }

  get tagPrefixPart(): string {
    return getPrefixFromString(this.tag);
  }

  get tagPostfixPart(): string {
    return getPostfixFromString(this.tag);
  }

  set tagPostfixPart(newValue: string) {
    this.setNewTag(this.tagIndex, this.tagSize, newValue);
  }

  setNewTag(index: number, size: number, tagPostfixPart: string) {
    const tagPrefixPart = this.tagPrefixPart;

    if (tagPostfixPart && size > 1) {
      logger.log(`invalid postfix: ${tagPostfixPart} ${this.tag}`);
      tagPostfixPart = "";
    }

    switch (size) {
      case 1:
        this.tag = `${tagPrefixPart}${index}${tagPostfixPart}`;
        break;

      case 2:
        this.tag = `${tagPrefixPart}${index},${index + 1}`;
        break;

      default:
        this.tag = `${tagPrefixPart}${index}-${index + size - 1}`;
        break;
    }
  }

  get tagSize(): number {
    return getSizeFromString(this.tag);
  }

  set tagSize(newValue: number) {
    this.setNewTag(this.tagIndex, newValue, this.tagPostfixPart);
  }

  get tagIndex(): number {
    const matches = this.tag.match(/\d+/g);
    if (!matches || matches.length < 1) {
      return -1;
    } else {
      if (this.tag.includes("-") || this.tag.includes(",")) {
        return parseInt(matches[0]);
      } else {
        return parseInt(matches[matches.length - 1]);
      }
    }
  }

  set tagIndex(newValue: number) {
    this.setNewTag(newValue, this.tagSize, this.tagPostfixPart);
  }

  compareWithOtherObject(other: BaseModel, otherState: State): boolean {
    let res = true;
    let propNames = this.getPersistentProperties();
    propNames.forEach((propName) => {
      const prop = (this as any)[propName];
      if (prop && prop.className == "BaseModelMap") {
        const thisBaseModelMap = prop as BaseModelMap<BaseModel>;
        const otherBaseModelMap = (other as any)[propName] as BaseModelMap<BaseModel>;
        if (!thisBaseModelMap.compareWithOtherMap(otherBaseModelMap, otherState)) {
          this.compareStatus |= enumCompareStatus.childModified;
          res = false;
        }
      }
    });

    propNames = this.getCompareProperties();

    propNames.forEach((propName) => {
      const thisProp = (this as any)[propName];
      const otherProp = (other as any)[propName];
      if (thisProp.compareWithOtherObject) {
        if (!thisProp.compareWithOtherObject(otherProp)) {
          this.compareStatus |= enumCompareStatus.modified;
          res = false;
        }
      } else {
        if (thisProp != otherProp) {
          this.compareStatus |= enumCompareStatus.modified;
          res = false;
        }
      }
    });
    return res;
  }

  logConsistencyError(message: string, userError?: string, action?: () => void) {
    state.logConsistencyError(this, message, userError || "", action);
  }

  consistencyCheck(parent: BaseModel) {
    if (this.parent != parent) {
      this.logConsistencyError(
        `Invalid parent:  should be: ${parent.className || ""}: ${parent.simpleFullTag} is: ${this.parent?.className || ""}: ${this.parent?.simpleFullTag ?? "parent undefined"}`,
      ); // no itx
    }

    const parentCollectionName = this.getParentCollectionName();

    if (parentCollectionName) {
      const collection = (this.parent as any)[parentCollectionName] as BaseModelMap<BaseModel>;
      if (collection) {
        const objInCollection = collection.get(this.id);

        if (objInCollection != this) {
          this.logConsistencyError("Object not in parent collection"); // no itx
          collection.add(this);
        }
      } else {
        this.logConsistencyError("Parent collection not found"); // no itx
      }
    }

    // nothing found
  }
}
